import { useCallback, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Carregando, Erro, Vazio } from '../../web/src/componentes/estados';
import { ErroRequisicao } from '../../web/src/nucleo/api';
import { definirAoExpirar, ppost } from './api';
import { trocarIdioma } from './i18n';
import { useClienteDoLink } from './cliente-do-link';
import { guardarDestino } from './destino';
import { cofre, ouvirLinks } from './nativo';
import { push, sairDoPortal } from './push';
import { CHAVE_SESSAO, ProvedorSessao, useSessaoQuery } from './sessao';
import { useTempoRealPortal } from './tempo-real';
import { Documentos } from './telas/Documentos';
import { Empresas } from './telas/Empresas';
import { FaturaDetalhe, Financeiro } from './telas/Financeiro';
import { Hoje } from './telas/Hoje';
import { Mensagens } from './telas/Mensagens';
import { Perfil } from './telas/Perfil';
import { RelatorioDetalhe, Relatorios } from './telas/Relatorios';
import { Saida } from './telas/Saida';
import { Convite, Entrar, Moldura, VerComo } from './telas/publicas';
import { Shell } from './telas/Shell';
import { TermoPendente } from './telas/TermoPendente';

function useSair() {
  const qc = useQueryClient();
  const navegar = useNavigate();
  return useCallback(async () => {
    await sairDoPortal({ push, sair: (corpo) => ppost('/acesso/sair', corpo), apagarToken: () => cofre.apagarToken() });
    qc.clear();
    navegar('/entrar', { replace: true });
  }, [qc, navegar]);
}

function NaoEncontrado() {
  const { t } = useTranslation();
  const { t: tp } = useTranslation('portal');
  const local = useLocation();
  // Review r52 (VIS-M2): o link da fatura era de um cliente que esta conta não acessa (aqui a seção pode nem existir).
  if ((local.state as { clienteNaoEncontrado?: boolean } | null)?.clienteNaoEncontrado) return <Vazio titulo={tp('financeiro.outraEmpresaTitulo')} texto={tp('financeiro.outraEmpresa')} />;
  return <Vazio titulo={t('estados.naoEncontrado')} texto="" />;
}

/** Sem sessão: guarda o destino (o link do e-mail de entrada abre noutra aba) e vai à entrada com ele no `state`. */
function SemSessao({ voltar }: { voltar: string }) {
  useEffect(() => guardarDestino(voltar), [voltar]);
  return <Navigate to="/entrar" replace state={{ voltar }} />;
}

/** Área logada: sessão do portal válida; termo novo pendente vem antes de tudo. */
function Protegido() {
  const q = useSessaoQuery();
  const local = useLocation();
  const sair = useSair();
  const { t, i18n } = useTranslation('portal');
  const ativo = !!q.data && !q.data.termo_pendente;
  useTempoRealPortal(ativo);
  // O idioma da conta (escolhido no Perfil, em qualquer aparelho) vale quando o aparelho não tem um salvo.
  useEffect(() => {
    const idioma = q.data?.conta?.idioma;
    let salvo: string | null = null;
    try {
      salvo = localStorage.getItem('malgo.portal.idioma');
    } catch {
      salvo = null;
    }
    if (idioma && !salvo && idioma !== i18n.language.slice(0, 2)) void trocarIdioma(idioma);
  }, [q.data?.conta?.idioma, i18n.language]);
  const troca = useClienteDoLink(ativo);
  if (q.isPending) return <Carregando texto={t('app.carregando')} />;
  if (q.isError) {
    // Review r52 (VIS-M2, SPEC-B2): o destino inteiro (com o `?cliente=` do link da fatura) volta depois da entrada.
    if (q.error instanceof ErroRequisicao && q.error.status === 401) return <SemSessao voltar={local.pathname + local.search} />;
    return (
      <Moldura titulo={t('app.nome')}>
        <Erro erro={q.error} aoTentar={() => q.refetch()} />
      </Moldura>
    );
  }
  if (q.data.termo_pendente) return <TermoPendente aoSair={sair} />;
  if (troca.erro !== null)
    return (
      <Moldura titulo={t('app.nome')}>
        <Erro erro={troca.erro} aoTentar={troca.tentar} />
      </Moldura>
    );
  if (troca.trocando) return <Carregando texto={t('app.carregando')} />;
  return (
    <ProvedorSessao dados={q.data}>
      <Routes>
        <Route element={<Shell aoSair={sair} />}>
          <Route index element={q.data.saida ? <Saida /> : <Hoje />} />
          {q.data.secoes.financeiro && <Route path="financeiro" element={<Financeiro />} />}
          {q.data.secoes.financeiro && <Route path="financeiro/:id" element={<FaturaDetalhe />} />}
          {q.data.secoes.relatorios && <Route path="relatorios" element={<Relatorios />} />}
          {q.data.secoes.relatorios && <Route path="relatorios/:id" element={<RelatorioDetalhe />} />}
          <Route path="documentos" element={<Documentos />} />
          {q.data.secoes.mensagens && <Route path="mensagens" element={<Mensagens />} />}
          <Route path="perfil" element={<Perfil aoSair={sair} />} />
          <Route path="empresas" element={<Empresas />} />
          <Route path="*" element={<NaoEncontrado />} />
        </Route>
      </Routes>
    </ProvedorSessao>
  );
}

export function App() {
  const qc = useQueryClient();
  const navegar = useNavigate();
  // Sessão que caiu (revogada, vencida, cliente arquivado): volta à entrada sem tela quebrada.
  useEffect(() => {
    definirAoExpirar(() => {
      void cofre.apagarToken();
      qc.removeQueries({ queryKey: CHAVE_SESSAO });
      // Review r52 (VIS-M2): o 401 chega aqui antes do `Protegido`; o destino (o link da fatura) volta depois da entrada.
      const voltar = window.location.pathname + window.location.search;
      guardarDestino(voltar);
      navegar('/entrar', { replace: true, state: { voltar } });
    });
  }, [qc, navegar]);
  // App das lojas: link universal (https://portal.malgo.com.br/...) e `malgo://` abrem a rota certa.
  useEffect(() => ouvirLinks((rota) => navegar(rota)), [navegar]);
  return (
    <Routes>
      <Route path="/entrar" element={<Entrar />} />
      <Route path="/convite" element={<Convite />} />
      <Route path="/ver-como" element={<VerComo />} />
      <Route path="/*" element={<Protegido />} />
    </Routes>
  );
}
